import React from 'react';
import { useChartTooltip } from './useChartTooltip.jsx';

// Predicted probability against what actually happened. A perfectly calibrated forecaster's points
// sit on the diagonal; below it means overconfident, above means underconfident.
//
// Buckets arrive already folded to >= 0.5 by the backend, so the x axis starts at 0.5.

const WIDTH = 420;
const HEIGHT = 320;
const PAD = { top: 16, right: 18, bottom: 42, left: 46 };

const X_MIN = 0.5;
const X_MAX = 1;
const PLOT_W = WIDTH - PAD.left - PAD.right;
const PLOT_H = HEIGHT - PAD.top - PAD.bottom;

const xScale = p => PAD.left + ((p - X_MIN) / (X_MAX - X_MIN)) * PLOT_W;
const yScale = v => PAD.top + (1 - v) * PLOT_H;

export default function CalibrationChart({ buckets, title = 'Calibration', note }) {
  const tip = useChartTooltip();
  if (!buckets || !buckets.length) return null;

  const maxCount = Math.max(...buckets.map(b => b.count));
  const radius = count => 4 + 8 * Math.sqrt(count / maxCount);
  const points = buckets.map(b => ({ ...b, x: xScale(b.probability), y: yScale(b.actualRate) }));
  const path = points.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  const xTicks = [0.5, 0.6, 0.7, 0.8, 0.9, 1];
  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <figure className="pred-chart" ref={tip.ref}>
      <figcaption className="pred-chart-title">{title}</figcaption>
      <p className="pred-legend">
        <span className="pred-legend-item">
          <span className="pred-legend-swatch is-primary" aria-hidden="true" />
          Your forecasts
        </span>
        <span className="pred-legend-item">
          <span className="pred-legend-swatch is-reference" aria-hidden="true" />
          Perfect calibration
        </span>
        <span className="pred-legend-note">closer to the line is better</span>
      </p>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="pred-chart-svg"
        role="img"
        aria-label="Predicted probability against observed frequency"
      >
        {yTicks.map(t => (
          <g key={`y${t}`}>
            <line
              className="pred-chart-grid"
              x1={PAD.left}
              x2={WIDTH - PAD.right}
              y1={yScale(t)}
              y2={yScale(t)}
            />
            <text className="pred-chart-tick" x={PAD.left - 8} y={yScale(t) + 4} textAnchor="end">
              {Math.round(t * 100)}%
            </text>
          </g>
        ))}

        {xTicks.map(t => (
          <text
            key={`x${t}`}
            className="pred-chart-tick"
            x={xScale(t)}
            y={HEIGHT - PAD.bottom + 18}
            textAnchor="middle"
          >
            {Math.round(t * 100)}%
          </text>
        ))}

        {/* Perfect calibration */}
        <line
          className="pred-chart-reference"
          x1={xScale(X_MIN)}
          y1={yScale(X_MIN)}
          x2={xScale(X_MAX)}
          y2={yScale(X_MAX)}
        />

        <path className="pred-chart-line" d={path} />

        {points.map(p => (
          <g key={p.probability}>
            <circle className="pred-chart-point" cx={p.x} cy={p.y} r={radius(p.count)} />
            {/* The smallest bucket draws at r=4, so the hover target is a fixed larger circle
                rather than the mark itself. */}
            <circle
              cx={p.x}
              cy={p.y}
              r={Math.max(radius(p.count), 13)}
              fill="transparent"
              {...tip.bind({
                title: `Said ${Math.round(p.probability * 100)}%`,
                rows: [
                  ['Predictions', p.count],
                  ['Happened', `${p.happened} (${Math.round(p.actualRate * 100)}%)`]
                ]
              })}
            />
          </g>
        ))}

        <text className="pred-chart-axis" x={PAD.left + PLOT_W / 2} y={HEIGHT - 6} textAnchor="middle">
          Predicted probability
        </text>
        <text
          className="pred-chart-axis"
          transform={`translate(12 ${PAD.top + PLOT_H / 2}) rotate(-90)`}
          textAnchor="middle"
        >
          Actually happened
        </text>
      </svg>
      {tip.node}
      <p className="pred-chart-note">
        {note ||
          'Dot size = predictions in that bucket. Forecasts below 0.5 are counted as their complement.'}
      </p>
    </figure>
  );
}
